// AGC: useWarehouse Hook
// AGC: Manages warehouses, containers (silos/tanks), and per-warehouse balances.
// AGC: All heavy validation and transactions happen on the backend.
// AGC: This hook owns the UI filter state (all / specific warehouse / grouped).

import { useState, useEffect, useCallback, useMemo } from 'react'
import { getBackendUrl } from '../utils/backendUrl'
import { useAuth } from './useAuth'
import type {
  Warehouse,
  Container,
  ContainerType,
  InventoryBalance,
  ContainerBalance,
} from '../types/inventory'

// =============================================================================
// AGC: HELPER: canonical unit conversion on the frontend
// AGC: Mirrors unitConversion.js — keeps UI independent of backend import
// =============================================================================

const UNIT_FACTORS: Record<string, { factor: number; canonical: 'kg' | 'L' }> = {
  ton: { factor: 1000, canonical: 'kg' },
  t: { factor: 1000, canonical: 'kg' },
  g: { factor: 0.001, canonical: 'kg' },
  sacas: { factor: 60, canonical: 'kg' },
  saca: { factor: 60, canonical: 'kg' },
  bags: { factor: 60, canonical: 'kg' },
  gal: { factor: 3.785411784, canonical: 'L' },
  gallon: { factor: 3.785411784, canonical: 'L' },
  gallons: { factor: 3.785411784, canonical: 'L' },
  mL: { factor: 0.001, canonical: 'L' },
  ml: { factor: 0.001, canonical: 'L' },
  kg: { factor: 1, canonical: 'kg' },
  L: { factor: 1, canonical: 'L' },
  l: { factor: 1, canonical: 'L' },
}

export function toCanonical(value: number, unit: string): number {
  const entry = UNIT_FACTORS[unit] || UNIT_FACTORS[unit.toLowerCase()]
  if (!entry) return value
  return value * entry.factor
}

export function fromCanonical(canonicalValue: number, displayUnit: string): number {
  const entry = UNIT_FACTORS[displayUnit] || UNIT_FACTORS[displayUnit.toLowerCase()]
  if (!entry || entry.factor === 0) return canonicalValue
  return canonicalValue / entry.factor
}

// =============================================================================
// AGC: TYPES
// =============================================================================

export type WarehouseFilter = 'all' | 'grouped' | string // AGC: 'all', 'grouped', or warehouseId

export interface UseWarehouseReturn {
  warehouses: Warehouse[]
  containers: Container[]
  balances: InventoryBalance[]
  containerBalances: ContainerBalance[]
  loading: boolean
  error: string | null
  hasMultipleWarehouses: boolean
  selectedWarehouseFilter: WarehouseFilter
  setWarehouseFilter: (filter: WarehouseFilter) => void
  // AGC: Warehouse CRUD
  createWarehouse: (data: { name: string; location?: { lat: number; lng: number; address?: string }; notes?: string }) => Promise<Warehouse>
  updateWarehouse: (id: string, updates: Partial<Pick<Warehouse, 'name' | 'location' | 'notes'>>) => Promise<Warehouse>
  archiveWarehouse: (id: string) => Promise<void>
  // AGC: Container CRUD
  createContainer: (data: {
    type: ContainerType; warehouseId: string; capacityValue: number; capacityUnit: string;
    assignedProductId?: string; assignedProductName?: string
  }) => Promise<Container>
  updateContainer: (id: string, updates: Partial<Container> & { capacityValue?: number }) => Promise<Container>
  archiveContainer: (id: string) => Promise<void>
  // AGC: Movements
  recordContainerMovement: (containerId: string, movement: {
    inventoryItemId: string; type: 'entry' | 'exit'; quantityDisplay: number;
    quantityUnit: string; reason?: string; warehouseId: string;
    sourceChannel?: string; rawCommandText?: string
  }) => Promise<void>
  fetchContainerMovements: (containerId: string, limit?: number) => Promise<import('../types/inventory').InventoryMovement[]>
  transferItem: (params: {
    itemId: string; fromWarehouseId: string; toWarehouseId: string;
    quantity: number; quantityUnit?: string; fromContainerId?: string;
    toContainerId?: string; reason?: string
  }) => Promise<string>
  // AGC: Helpers
  refresh: () => Promise<void>
  getContainersForWarehouse: (warehouseId: string, type?: ContainerType) => Container[]
  getBalanceForItemInWarehouse: (itemId: string, warehouseId: string) => number
  getCapacityPercent: (container: Container) => number
  getCapacityStatus: (percent: number) => 'ok' | 'warning' | 'full'
  convertToDisplayUnit: (canonicalValue: number, displayUnit: string) => number
  convertToCanonicalUnit: (displayValue: number, unit: string) => number
}

// =============================================================================
// AGC: MAIN HOOK
// =============================================================================

export const useWarehouse = (): UseWarehouseReturn => {
  const { user, getAuthToken } = useAuth()
  const backendUrl = getBackendUrl()

  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [containers, setContainers] = useState<Container[]>([])
  const [balances, setBalances] = useState<InventoryBalance[]>([])
  const [containerBalances, setContainerBalances] = useState<ContainerBalance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedWarehouseFilter, setWarehouseFilter] = useState<WarehouseFilter>('all')

  // AGC: Authenticated fetch helper
  const authFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = await getAuthToken()
    if (!token) throw new Error('No auth token')
    return fetch(`${backendUrl}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    })
  }, [backendUrl, getAuthToken])

  // AGC: Fetch all warehouse data in parallel
  const fetchAll = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      const [whRes, cRes, cbRes] = await Promise.all([
        authFetch('/api/v1/warehouses'),
        authFetch('/api/v1/warehouses/containers/list'),
        authFetch('/api/v1/warehouses/balances/containers'),
      ])

      const [whData, cData, cbData] = await Promise.all([whRes.json(), cRes.json(), cbRes.json()])

      if (whData.success) setWarehouses(whData.data || [])
      if (cData.success) setContainers(cData.data || [])
      if (cbData.success) setContainerBalances(cbData.data || [])
    } catch (err: any) {
      console.error('❌ AGC: [useWarehouse] Error fetching warehouse data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [user, authFetch])

  useEffect(() => { fetchAll() }, [fetchAll])

  const refresh = useCallback(async () => { await fetchAll() }, [fetchAll])

  const hasMultipleWarehouses = useMemo(() => warehouses.length > 1, [warehouses])

  // =============================================================================
  // AGC: WAREHOUSE CRUD
  // =============================================================================

  const createWarehouse = useCallback(async (data: { name: string; location?: any; notes?: string }): Promise<Warehouse> => {
    const res = await authFetch('/api/v1/warehouses', { method: 'POST', body: JSON.stringify(data) })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    setWarehouses(prev => [...prev, json.data])
    return json.data
  }, [authFetch])

  const updateWarehouse = useCallback(async (id: string, updates: any): Promise<Warehouse> => {
    const res = await authFetch(`/api/v1/warehouses/${id}`, { method: 'PUT', body: JSON.stringify(updates) })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    setWarehouses(prev => prev.map(w => w.id === id ? { ...w, ...json.data } : w))
    return json.data
  }, [authFetch])

  const archiveWarehouse = useCallback(async (id: string): Promise<void> => {
    const res = await authFetch(`/api/v1/warehouses/${id}`, { method: 'DELETE' })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    setWarehouses(prev => prev.filter(w => w.id !== id))
    // AGC: Reset filter if deleted warehouse was selected
    if (selectedWarehouseFilter === id) setWarehouseFilter('all')
  }, [authFetch, selectedWarehouseFilter])

  // =============================================================================
  // AGC: CONTAINER CRUD
  // =============================================================================

  const createContainer = useCallback(async (data: any): Promise<Container> => {
    const res = await authFetch('/api/v1/warehouses/containers', { method: 'POST', body: JSON.stringify(data) })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    setContainers(prev => [...prev, json.data])
    return json.data
  }, [authFetch])

  const updateContainer = useCallback(async (id: string, updates: any): Promise<Container> => {
    const res = await authFetch(`/api/v1/warehouses/containers/${id}`, { method: 'PUT', body: JSON.stringify(updates) })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    setContainers(prev => prev.map(c => c.id === id ? { ...c, ...json.data } : c))
    return json.data
  }, [authFetch])

  const archiveContainer = useCallback(async (id: string): Promise<void> => {
    const res = await authFetch(`/api/v1/warehouses/containers/${id}`, { method: 'DELETE' })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    setContainers(prev => prev.filter(c => c.id !== id))
    setContainerBalances(prev => prev.filter(cb => cb.containerId !== id))
  }, [authFetch])

  // =============================================================================
  // AGC: MOVEMENTS
  // =============================================================================

  const recordContainerMovement = useCallback(async (containerId: string, movement: any): Promise<void> => {
    const res = await authFetch(`/api/v1/warehouses/containers/${containerId}/movement`, {
      method: 'POST',
      body: JSON.stringify(movement),
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    // AGC: Refresh containers and balances to get latest levels
    await fetchAll()
  }, [authFetch, fetchAll])

  // AGC: Fetch movement history for a specific container (shown in SiloTankDetailModal)
  const fetchContainerMovements = useCallback(async (containerId: string, limit = 20) => {
    const res = await authFetch(`/api/v1/warehouses/containers/${containerId}/movements?limit=${limit}`)
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    return json.data || []
  }, [authFetch])

  const transferItem = useCallback(async (params: any): Promise<string> => {
    const res = await authFetch('/api/v1/warehouses/transfer', {
      method: 'POST',
      body: JSON.stringify(params),
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message)
    await fetchAll()
    return json.data?.transferId || ''
  }, [authFetch, fetchAll])

  // =============================================================================
  // AGC: HELPERS
  // =============================================================================

  const getContainersForWarehouse = useCallback((warehouseId: string, type?: ContainerType): Container[] => {
    return containers.filter(c => c.warehouseId === warehouseId && (!type || c.type === type))
  }, [containers])

  const getBalanceForItemInWarehouse = useCallback((itemId: string, warehouseId: string): number => {
    const balance = balances.find(b => b.itemId === itemId && b.warehouseId === warehouseId)
    return balance?.quantity ?? 0
  }, [balances])

  const getCapacityPercent = useCallback((container: Container): number => {
    if (!container.capacityCanonical || container.capacityCanonical <= 0) return 0
    return Math.min(100, Math.max(0, (container.currentLevel / container.capacityCanonical) * 100))
  }, [])

  const getCapacityStatus = useCallback((percent: number): 'ok' | 'warning' | 'full' => {
    if (percent >= 90) return 'full'
    if (percent >= 70) return 'warning'
    return 'ok'
  }, [])

  const convertToDisplayUnit = useCallback((canonicalValue: number, displayUnit: string): number => {
    return fromCanonical(canonicalValue, displayUnit)
  }, [])

  const convertToCanonicalUnit = useCallback((displayValue: number, unit: string): number => {
    return toCanonical(displayValue, unit)
  }, [])

  return {
    warehouses,
    containers,
    balances,
    containerBalances,
    loading,
    error,
    hasMultipleWarehouses,
    selectedWarehouseFilter,
    setWarehouseFilter,
    createWarehouse,
    updateWarehouse,
    archiveWarehouse,
    createContainer,
    updateContainer,
    archiveContainer,
    recordContainerMovement,
    fetchContainerMovements,
    transferItem,
    refresh,
    getContainersForWarehouse,
    getBalanceForItemInWarehouse,
    getCapacityPercent,
    getCapacityStatus,
    convertToDisplayUnit,
    convertToCanonicalUnit,
  }
}

export default useWarehouse
